import React, { useState, useCallback, Fragment, useRef, memo } from "react"

// ** Third Party Components
import axios from "axios"
import { Loader } from "lucide-react"
import { parseCookies } from "nookies"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { CloseCircle, VideoPlay, Document, Add, Edit2, TickCircle } from "iconsax-reactjs"

// ** Helpers
import { cn, formatFileSize, generateId, getFileExtension, getFileIconConfig, getFileNameWithoutExtension } from "@pma/utils/helpers"

// ** Types & Constants
import { DictType, FileItem, FilePreviewCardProps, ProjectFile } from "@pma/types"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Services
import { uploadFilesInProjectService } from "@pma/services/files"

// ** Routes
import { DIGITAL_OCEAN_FILES_ENDPOINT } from "@pma/utils/api-routes"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Custom Components
import { showDeletePermissionToast } from "@pma/pages/Project/Task/Common/DeletePermissionToast"

// ** Types

type UploadStatus = "idle" | "uploading" | "success" | "error"

// ** File Preview Card Component (Memoized & Extracted)
const FilePreviewCard = memo(
  ({
    fileItem,
    index,
    fileName,
    isUploading,
    editingFileId,
    onRemove,
    onNameChange,
    onEditStart,
    onEditEnd,
  }: FilePreviewCardProps) => {
    const isImage = fileItem.file.type.startsWith("image/")
    const isVideo = fileItem.file.type.startsWith("video/")
    const iconConfig = getFileIconConfig(fileItem.file)
    const IconComponent = iconConfig.icon
    const isEditing = editingFileId === fileItem.id
    const fileExtension = getFileExtension(fileItem.file.name)

    // Initialize with the current fileName prop (fixes first-time edit issue)
    const [tempName, setTempName] = useState(fileName)

    const handleStartEdit = useCallback(() => {
      if (isUploading) return
      // Use fileName prop which always has the correct value
      setTempName(fileName)
      onEditStart(fileItem.id)
    }, [isUploading, fileName, fileItem.id, onEditStart])

    const handleSaveEdit = useCallback(() => {
      if (tempName.trim()) {
        onNameChange(index, tempName.trim())
      }
      onEditEnd()
    }, [tempName, index, onNameChange, onEditEnd])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter") handleSaveEdit()
        else if (e.key === "Escape") onEditEnd()
      },
      [handleSaveEdit, onEditEnd],
    )

    return (
      <div className='group relative flex flex-col overflow-visible'>
        {/* Preview Container */}
        <div
          className={cn(
            "relative aspect-square overflow-hidden rounded-xl border border-neutral-200 bg-white",
            isUploading && "opacity-60",
          )}
        >
          {/* Image Preview */}
          {isImage && fileItem.preview && (
            <img
              src={fileItem.preview}
              alt={fileItem.file.name}
              className='h-full w-full object-cover'
            />
          )}

          {/* Video Preview */}
          {isVideo && fileItem.preview && (
            <div className='relative h-full w-full'>
              <video src={fileItem.preview} className='h-full w-full object-cover' muted />
              <div className='absolute inset-0 flex items-center justify-center bg-black/30'>
                <div className='rounded-full bg-white/90 p-2'>
                  <VideoPlay size={24} className='text-purple-600' variant='Bold' />
                </div>
              </div>
            </div>
          )}

          {/* Other File Types - Icon Preview */}
          {!isImage && !isVideo && (
            <div className={cn("flex h-full w-full items-center justify-center", iconConfig.bg)}>
              <IconComponent size={40} className={iconConfig.color} variant='Bold' />
            </div>
          )}

          {/* Remove Button */}
          {!isUploading && (
            <button
              onClick={() => onRemove(fileItem.id, index)}
              className='absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 transition-all hover:bg-black/80 md:opacity-0 md:group-hover:opacity-100'
            >
              <CloseCircle size={16} className='text-white' variant='Bold' />
            </button>
          )}
        </div>

        {/* File Info */}
        <div className='mt-2 px-0.5'>
          {isEditing ? (
            <div className='flex items-center gap-1'>
              <input
                type='text'
                value={tempName}
                onChange={(e) => setTempName(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSaveEdit}
                autoFocus
                className='border-primary-300 focus:border-primary-500 w-full rounded border px-1 py-0.5 text-xs font-medium text-neutral-700 outline-none'
                dir='auto'
              />
              <button
                onClick={handleSaveEdit}
                className='flex-shrink-0 rounded p-0.5 hover:bg-neutral-100'
              >
                <TickCircle size={14} className='text-green-500' variant='Bold' />
              </button>
            </div>
          ) : (
            <div className='flex items-center gap-1'>
              <p
                className={cn(
                  "flex-1 truncate text-xs font-medium text-neutral-700",
                  !isUploading && "hover:text-primary-600 cursor-pointer",
                )}
                title={`${fileName}.${fileExtension}`}
                dir='auto'
                onClick={handleStartEdit}
              >
                {fileName}
                <span className='text-neutral-400'>.{fileExtension}</span>
              </p>
              {!isUploading && (
                <button
                  onClick={handleStartEdit}
                  className='flex-shrink-0 rounded p-0.5 opacity-0 transition-opacity hover:bg-neutral-100 group-hover:opacity-100'
                >
                  <Edit2 size={12} className='text-neutral-400' />
                </button>
              )}
            </div>
          )}
          <p className='text-[10px] text-neutral-400'>{formatFileSize(fileItem.file.size)}</p>
        </div>
      </div>
    )
  },
)

FilePreviewCard.displayName = "FilePreviewCard"

// ** Progress Bar Component
const ProgressBar = memo(
  ({
    progress,
    status,
    translate,
  }: {
    progress: number
    status: UploadStatus
    translate: (key: string) => string
  }) => {
    const getProgressColor = () => {
      switch (status) {
        case "success":
          return "bg-green-500"
        case "error":
          return "bg-red-500"
        default:
          return "bg-blue-500"
      }
    }

    const getTextColor = () => {
      switch (status) {
        case "success":
          return "text-green-600"
        case "error":
          return "text-red-600"
        default:
          return "text-blue-600"
      }
    }

    return (
      <div className='border-t border-neutral-100 px-5 py-3'>
        <div className='mb-2 flex items-center justify-between'>
          <span className='text-sm font-medium text-neutral-600'>
            {status === "error" ? translate("Upload failed") : translate("Uploading")}...
          </span>
          <span className={cn("text-sm font-semibold", getTextColor())}>{progress}%</span>
        </div>
        <div className='h-2 w-full overflow-hidden rounded-full bg-neutral-200'>
          <div
            className={cn(
              "h-full rounded-full transition-all duration-300 ease-out",
              getProgressColor(),
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
        {status === "success" && (
          <p className='mt-2 flex items-center gap-1 text-xs text-green-600'>
            <TickCircle size={14} variant='Bold' />
            {translate("Upload complete")}!
          </p>
        )}
        {status === "error" && (
          <p className='mt-2 flex items-center gap-1 text-xs text-red-600'>
            <CloseCircle size={14} variant='Bold' />
            {translate("Upload failed. Please try again")}
          </p>
        )}
      </div>
    )
  },
)

ProgressBar.displayName = "ProgressBar"

// ** Main Component
interface UploadFilesProps {
  onClose: () => void
  isMobile?: boolean
}

const UploadFiles = ({ onClose, isMobile }: UploadFilesProps) => {
  const { folderId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { translate } = useTranslator()
  const { authConfig } = useAuth()

  const fileUploadCount = useRef<number>(0)
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const isAdmin = authConfig?.companies_of_user?.find((company: any) => {
    if (
      company?.id === authConfig?.last_organization_id &&
      company?.role?.id == 22 /* ADMIN_ROLE_ID */
    )
      return true
  })

  const [progress, setProgress] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([])
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle")
  const [editingFileId, setEditingFileId] = useState<string | null>(null)
  const [updatedFilesNameList, setUpdatedFilesNameList] = useState<string[]>([])

  const isUploading = uploadStatus === "uploading"

  const cleanupPreview = useCallback((file: FileItem) => {
    if (file.preview) URL.revokeObjectURL(file.preview)
  }, [])

  const processFiles = useCallback((files: FileList) => {
    const newFiles: FileItem[] = Array.from(files).map((file) => ({
      file,
      id: generateId(),
      ...(file.type.startsWith("image/") || file.type.startsWith("video/")
        ? { preview: URL.createObjectURL(file) }
        : {}),
    }))

    setSelectedFiles((prev) => [...prev, ...newFiles])
    setUpdatedFilesNameList((prev) => [
      ...prev,
      ...Array.from(files).map((file) => getFileNameWithoutExtension(file.name)),
    ])
  }, [])

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!isUploading) setIsDragging(true)
    },
    [isUploading],
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      if (isUploading) return

      const files = e.dataTransfer.files
      if (files.length > 0) processFiles(files)
    },
    [processFiles, isUploading],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isUploading) return

      const files = e.target.files
      if (files?.length) processFiles(files)
      e.target.value = ""
    },
    [processFiles, isUploading],
  )

  const handleRemoveFile = useCallback(
    (id: string, index: number) => {
      if (isUploading) return

      setSelectedFiles((prev) => {
        const fileToRemove = prev.find((f) => f.id === id)
        if (fileToRemove) cleanupPreview(fileToRemove)
        return prev.filter((f) => f.id !== id)
      })
      setUpdatedFilesNameList((prev) => prev.filter((_, i) => i !== index))
    },
    [isUploading, cleanupPreview],
  )

  const handleFileNameChange = useCallback((index: number, newName: string) => {
    setUpdatedFilesNameList((prev) => {
      const updated = [...prev]
      updated[index] = newName
      return updated
    })
  }, [])

  const handleEditStart = useCallback((id: string) => setEditingFileId(id), [])
  const handleEditEnd = useCallback(() => setEditingFileId(null), [])

  const resetState = useCallback(() => {
    selectedFiles.forEach(cleanupPreview)
    setSelectedFiles([])
    setUpdatedFilesNameList([])
    setProgress(0)
    setUploadStatus("idle")
  }, [selectedFiles, cleanupPreview])

  const handleCancel = useCallback(() => {
    if (isUploading) return
    resetState()
    onClose()
  }, [isUploading, resetState, onClose])

  // ** API Handlers
  const handleUploadFilesApi = async () => {
    if (!selectedFiles.length) return

    const formData = new FormData()

    selectedFiles.forEach((val, index) => {
      const file = val.file
      const updatedName = updatedFilesNameList[index] || getFileNameWithoutExtension(file.name)
      const fileExt = getFileExtension(file.name)
      formData.append("files", new File([file], `${updatedName}.${fileExt}`, { type: file.type }))
    })

    const response = await axios.post(
      `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`,
      formData,
      {
        headers: { accept: "*/*", "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          setProgress(percentCompleted)
        },
      },
    )

    return response?.data
  }

  const uploadToProjectMutation = useMutation({
    mutationKey: ["add-files-to-project"],
    mutationFn: uploadFilesInProjectService,
    onSuccess: async () => {
      setUploadStatus("success")
      toast({
        title: translate("Success"),
        description: `${fileUploadCount.current} ${fileUploadCount.current === 1 ? translate("File uploaded successfully") : translate("Files uploaded successfully")}`,
        variant: "success",
      })

      fileUploadCount.current = 0
      await queryClient.invalidateQueries({ queryKey: ["get-sub-folders-and-files"] })

      if (location.pathname.includes("/files")) {
        await queryClient.invalidateQueries({ queryKey: ["get-parent-and-sub-folder-with-filter"] })
      }

      // Delay close to show success state
      setTimeout(() => {
        resetState()
        onClose()
      }, 1000)
    },
    onError: (error: DictType) => {
      setUploadStatus("error")
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errorMessage
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to add file at this time"),
        variant: "error",
      })
    },
  })

  const uploadFilesMutation = useMutation({
    mutationKey: ["upload-files"],
    mutationFn: handleUploadFilesApi,
    onSuccess: (data) => {
      const payload = data as ProjectFile[]
      fileUploadCount.current = payload.length

      uploadToProjectMutation.mutate({
        folder_id: folderId,
        inform_users: true,
        files_id: payload.map((file) => file.id),
      })
    },
    onError: (error: DictType) => {
      setUploadStatus("error")
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errorMessage
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to upload file at this time"),
        variant: "error",
      })
    },
  })

  const handleUpload = useCallback(() => {
    if (!selectedFiles.length || isUploading) return

    if (!isAdmin) {
      showDeletePermissionToast({
        message: translate(
          "No permission to upload files. Contact your project manager for access.",
        ),
        actionLabel: translate("Request Access"),
        onAction: () => {
          console.log("contact admin")
        },
      })
      return
    }

    setUploadStatus("uploading")
    setProgress(0)
    uploadFilesMutation.mutate()
  }, [selectedFiles.length, isUploading, uploadFilesMutation, isAdmin, translate, navigate])

  const hasFiles = selectedFiles.length > 0

  return (
    <Fragment>
      {/* Header */}
      {!isMobile ? (
        <div className='flex items-center justify-between border-b border-neutral-200 px-5 py-3'>
          <h2 className='font-[inter] text-base font-medium text-neutral-800'>
            {translate("Folder")} \ {translate("File Upload")}
          </h2>
          <button
            onClick={onClose}
            disabled={isUploading}
            className={cn(
              "rounded-full p-1 transition-colors hover:bg-neutral-100",
              isUploading && "cursor-not-allowed opacity-50",
            )}
          >
            <CloseCircle className='text-neutral-500' size={22} />
          </button>
        </div>
      ) : null}

      {/* Content Area */}
      <div
        className={cn(
          "max-h-[400px] overflow-y-auto px-5 py-4",
          isUploading && "pointer-events-none",
        )}
      >
        {!hasFiles ? (
          /* Empty State */
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              "flex min-h-[180px] flex-col items-center justify-center rounded-xl border border-[#E2E8F0] transition-all",
              isDragging ? "border-primary-500 bg-primary-50" : "bg-neutral-50/50",
              isUploading && "opacity-50",
            )}
          >
            <div className='mb-3 rounded-full bg-neutral-100 p-3'>
              <Document size={28} className='text-neutral-400' />
            </div>
            <p className='text-sm text-neutral-500'>
              {translate("Drag & Drop a Folder")} \ {translate("File")}
            </p>
            <label
              className={cn(
                "mt-4 cursor-pointer rounded-xl border border-[#F1F4F9] bg-white px-4 py-2 font-[inter] text-xs font-normal leading-[22px] text-[#1E2A3B] shadow-[0px_1px_2px_rgba(82,88,102,0.06)] transition-all hover:border-neutral-300 hover:bg-neutral-50",
                isUploading && "cursor-not-allowed opacity-50",
              )}
            >
              {translate("Choose a Folder")} \ {translate("File")}
              <input
                type='file'
                multiple
                className='hidden'
                onChange={handleFileSelect}
                disabled={isUploading}
              />
            </label>
          </div>
        ) : (
          /* Files Grid */
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              "rounded-xl border-2 border-dashed p-4 transition-all",
              isDragging
                ? "border-primary-500 bg-primary-50"
                : "bg-neutral-50/30 border-transparent",
            )}
          >
            <div className='grid grid-cols-3 gap-4 sm:grid-cols-4'>
              {selectedFiles.map((fileItem, index) => (
                <FilePreviewCard
                  key={fileItem.id}
                  fileItem={fileItem}
                  index={index}
                  fileName={
                    updatedFilesNameList[index] || getFileNameWithoutExtension(fileItem.file.name)
                  }
                  isUploading={isUploading}
                  editingFileId={editingFileId}
                  onRemove={handleRemoveFile}
                  onNameChange={handleFileNameChange}
                  onEditStart={handleEditStart}
                  onEditEnd={handleEditEnd}
                />
              ))}

              {/* Add More Button */}
              {!isUploading && (
                <label className='hover:border-primary-400 hover:bg-primary-50 flex aspect-square cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-300 bg-white transition-all'>
                  <Add size={28} className='text-neutral-400' />
                  <span className='mt-1 text-[11px] text-neutral-500'>{translate("Add More")}</span>
                  <input
                    type='file'
                    multiple
                    className='hidden'
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                </label>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {uploadStatus !== "idle" && (
        <ProgressBar progress={progress} status={uploadStatus} translate={translate} />
      )}

      {/* Footer */}
      <div className='flex items-center justify-between border-t border-neutral-200 px-5 py-4'>
        <span className='text-sm text-neutral-500'>
          {hasFiles ? `${selectedFiles.length} ${translate("file(s) selected")}` : ""}
        </span>
        <div className='flex gap-2'>
          <button
            onClick={handleCancel}
            disabled={isUploading}
            className={cn(
              "rounded-full border border-neutral-300 bg-white px-5 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50",
              isUploading && "cursor-not-allowed opacity-50",
            )}
          >
            {translate("Cancel")}
          </button>
          <button
            onClick={handleUpload}
            disabled={!hasFiles || isUploading}
            className={cn(
              "rounded-full px-5 py-2 text-sm font-medium text-white transition-all",
              hasFiles && !isUploading
                ? "bg-[radial-gradient(109.76%_151.9%_at_7.34%_35%,_#9B8AFB_0%,_#7F6AFF_44.27%)] hover:bg-[radial-gradient(109.91%_126.55%_at_13.06%_22.92%,_#7A5AF8_30.95%,_#6938EF_65.87%)] hover:opacity-90 active:bg-[radial-gradient(109.91%_126.55%_at_13.06%_22.92%,_#7A5AF8_30.95%,_#6938EF_65.87%)]"
                : "cursor-not-allowed bg-neutral-300",
            )}
          >
            {isUploading ? (
              <span className='flex items-center gap-2'>
                <Loader className='animate-spin' color='white' size={16} />
                {translate("Uploading")}...
              </span>
            ) : (
              translate("Upload")
            )}
          </button>
        </div>
      </div>
    </Fragment>
  )
}

export default UploadFiles
